import {
  ArrowLeft,
  CheckCircle2,
  Edit,
  FileSignature,
  Gauge,
  Loader2,
  MessageSquare,
  Trash2,
  UserCog,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { QrCodeDisplay } from '@/components/ui/qr-code-display';
import { Skeleton } from '@/components/ui/skeleton';
import { TransitionModal } from '@/components/ui/transition-modal';
import { useAuthStore } from '@/auth/store';
import { useEquipment } from '@/features/equipment/queries';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import {
  useDeleteWorkOrder,
  useTransitionWorkOrder,
  useWorkOrder,
  useWorkOrderAssignments,
  useWorkOrderEvents,
} from '@/features/work-orders/queries';
import {
  STATUSES_REQUIRING_REASON,
  availableTransitions,
  isWorkOrderEditable,
  workOrderPriorityColor,
  workOrderStatusColor,
  type WorkOrder,
  type WorkOrderAssignment,
  type WorkOrderEvent,
  type WorkOrderStatus,
} from '@/features/work-orders/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { WorkOrderCostCard } from './WorkOrderCostCard';
import { WorkOrderCounterAction } from './WorkOrderCounterAction';

function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

function formatDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/**
 * TimelineCard — renders the append-only event log (Phase 3c).
 *
 * Each event row is a small flex-row with:
 *   - a colored icon disc identifying the event type
 *   - the event-type label and (when present) a human-readable
 *     payload — status transitions (from → to), measurement labels
 *     and values, assignment add/remove
 *   - the comment body in italics for type=comment
 *   - the createdAt timestamp formatted in the user's locale
 *
 * Events come ordered ASC from the backend.
 */
function TimelineCard({ events }: { events: WorkOrderEvent[] }) {
  const { t } = useTranslation();

  if (events.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        {t('workOrder.timeline.empty')}
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {events.map((ev) => {
        const Icon = eventIcon(ev.type);
        const tone = eventTone(ev.type);
        return (
          <li key={ev.id} className="flex gap-3">
            <span
              className={cn(
                'mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                tone,
              )}
            >
              <Icon className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                <span className="font-medium">
                  {t(`workOrder.event.${ev.type}`)}
                </span>
                <span className="text-muted-foreground">
                  {renderPayloadSummary(ev, t)}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDateTime(ev.createdAt)}
                </span>
              </div>
              {ev.type === 'comment' && ev.body && (
                <p className="mt-1 whitespace-pre-line text-sm">{ev.body}</p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function eventIcon(type: WorkOrderEvent['type']) {
  switch (type) {
    case 'comment':
      return MessageSquare;
    case 'status_log':
      return CheckCircle2;
    case 'measurement':
      return Gauge;
    case 'signature':
      return FileSignature;
    case 'assignment_log':
      return UserCog;
  }
}

function eventTone(type: WorkOrderEvent['type']): string {
  switch (type) {
    case 'comment':
      return 'bg-sky-100 text-sky-700';
    case 'status_log':
      return 'bg-amber-100 text-amber-700';
    case 'measurement':
      return 'bg-purple-100 text-purple-700';
    case 'signature':
      return 'bg-emerald-100 text-emerald-700';
    case 'assignment_log':
      return 'bg-slate-100 text-slate-700';
  }
}

/** Render a one-line summary of the structured payload, depending
 *  on event type. Comments are not summarised — their body is shown
 *  on its own line below the header. */
function renderPayloadSummary(
  ev: WorkOrderEvent,
  t: (k: string, vars?: Record<string, unknown>) => string,
): string {
  const p = ev.payload ?? {};
  if (ev.type === 'status_log' && 'from' in p && 'to' in p) {
    return t('workOrder.event.statusTransition', {
      from: t(`workOrder.status.${p.from as string}`),
      to: t(`workOrder.status.${p.to as string}`),
    });
  }
  if (ev.type === 'measurement' && 'label' in p && 'value' in p) {
    return t('workOrder.event.measurementValue', {
      label: p.label as string,
      value: String(p.value),
      unit: (p.unit as string | undefined) ?? '',
    });
  }
  if (ev.type === 'assignment_log' && 'action' in p) {
    return t(
      `workOrder.event.assignmentAction.${p.action as 'assigned' | 'unassigned'}`,
    );
  }
  return '';
}

/**
 * AssignmentsCard — list of currently active assignments. Past
 * assignments (those with unassignedAt set) are still returned by
 * the backend so the timeline can show their history; the card
 * itself filters them out and only shows active rows.
 */
function AssignmentsCard({
  assignments,
}: {
  assignments: WorkOrderAssignment[];
}) {
  const { t } = useTranslation();
  const active = assignments.filter((a) => a.unassignedAt === null);

  if (active.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('workOrder.assignment.empty')}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {active.map((a) => (
        <li
          key={a.id}
          className="flex items-center justify-between rounded-md border p-2 text-sm"
        >
          <div className="flex items-center gap-2">
            <UserCog className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span className="font-mono text-xs text-muted-foreground">
              {a.userId.slice(0, 8)}
            </span>
            <span>
              {t(`workOrder.assignment.role.${a.role}`, {
                defaultValue: a.role,
              })}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDate(a.assignedAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DetailContent({ wo }: { wo: WorkOrder }) {
  const { t } = useTranslation();
  const { data: vessels } = useVessels();
  const { data: vesselEquipment } = useEquipment({ vesselId: wo.vesselId });

  const equipment = useMemo(
    () => vesselEquipment?.find((e) => e.id === wo.equipmentId) ?? null,
    [vesselEquipment, wo.equipmentId],
  );
  const vesselName = useMemo(() => {
    const v = vessels?.find((x) => x.id === wo.vesselId);
    return v ? vesselDisplayName(v) : null;
  }, [vessels, wo.vesselId]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('workOrder.detail.sectionIdentity')}>
        <Field
          label={t('workOrder.fields.number')}
          value={<span className="font-mono">{wo.number}</span>}
        />
        <Field label={t('workOrder.fields.title')} value={wo.title} />
        <Field
          label={t('workOrder.fields.description')}
          value={
            wo.description ? (
              <p className="whitespace-pre-line">{wo.description}</p>
            ) : null
          }
          fullWidth
        />
      </Section>

      <Section title={t('workOrder.detail.sectionTarget')}>
        <Field
          label={t('workOrder.fields.equipmentId')}
          value={
            equipment ? (
              <Link
                to={`/equipment/${equipment.id}`}
                className="text-primary hover:underline"
              >
                <span className="font-mono">{equipment.tag}</span>{' '}
                — {equipment.name}
              </Link>
            ) : null
          }
        />
        <Field
          label={t('workOrder.fields.vesselId')}
          value={
            vesselName ? (
              <Link
                to={`/vessels/${wo.vesselId}`}
                className="text-primary hover:underline"
              >
                {vesselName}
              </Link>
            ) : null
          }
        />
        <Field
          label={t('workOrder.fields.type')}
          value={t(`workOrder.type.${wo.type}`)}
        />
        <Field
          label={t('workOrder.fields.priority')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                workOrderPriorityColor(wo.priority),
              )}
            >
              {t(`workOrder.priority.${wo.priority}`)}
            </span>
          }
        />
      </Section>

      <Section title={t('workOrder.detail.sectionPlanning')}>
        <Field
          label={t('workOrder.fields.plannedStartAt')}
          value={formatDate(wo.plannedStartAt)}
        />
        <Field
          label={t('workOrder.fields.dueAt')}
          value={formatDate(wo.dueAt)}
        />
        <Field
          label={t('workOrder.fields.estimatedHours')}
          value={wo.estimatedHours}
        />
        <Field
          label={t('workOrder.fields.actualHours')}
          value={wo.actualHours}
        />
      </Section>

      <Section title={t('workOrder.detail.sectionLifecycle')}>
        <Field
          label={t('workOrder.fields.startedAt')}
          value={formatDateTime(wo.startedAt)}
        />
        <Field
          label={t('workOrder.fields.completedAt')}
          value={formatDateTime(wo.completedAt)}
        />
        <Field
          label={t('workOrder.fields.signedAt')}
          value={formatDateTime(wo.signedAt)}
        />
        <Field
          label={t('workOrder.fields.closedAt')}
          value={formatDateTime(wo.closedAt)}
        />
        {wo.cancelledAt && (
          <>
            <Field
              label={t('workOrder.fields.cancelledAt')}
              value={formatDateTime(wo.cancelledAt)}
            />
            <Field
              label={t('workOrder.fields.cancelReason')}
              value={wo.cancelReason}
              fullWidth
            />
          </>
        )}
      </Section>
    </div>
  );
}

export function WorkOrderDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: wo, isLoading, isError, error, refetch } = useWorkOrder(id);
  const { data: events, isLoading: eventsLoading } = useWorkOrderEvents(id);
  const { data: assignments, isLoading: assignmentsLoading } =
    useWorkOrderAssignments(id);
  const deleteMut = useDeleteWorkOrder();
  const transitionMut = useTransitionWorkOrder(id ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transitionTarget, setTransitionTarget] =
    useState<WorkOrderStatus | null>(null);
  const user = useAuthStore((s) => s.user);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('workOrder.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('workOrder.detail.loadError');
  })();

  const onConfirmDelete = () => {
    if (!id) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/work-orders');
      },
    });
  };

  const editable = wo ? isWorkOrderEditable(wo.status) : false;

  // Available transitions for the current user, filtered by allowed
  // roles. requiresAssignment is enforced by the backend.
  const transitions = wo
    ? availableTransitions(wo.status, user?.roles ?? [])
    : [];

  // Visual variant for each transition button. Forward-progress
  // transitions (open, in_progress, pending_review, closed) get
  // the default primary look; destructive ones (cancelled, rejected)
  // get the destructive look; backward (draft) gets outline.
  const variantFor = (to: WorkOrderStatus): 'default' | 'destructive' | 'outline' => {
    if (to === 'cancelled' || to === 'rejected') return 'destructive';
    if (to === 'draft') return 'outline';
    return 'default';
  };

  const transitionErrorMessage = (() => {
    if (!transitionMut.error) return null;
    if (transitionMut.error instanceof ApiError) {
      return t(transitionMut.error.key, {
        defaultValue: t('workOrder.transition.failed'),
        ...(transitionMut.error.params ?? {}),
      });
    }
    return t('workOrder.transition.failed');
  })();

  const onConfirmTransition = (input: { reason?: string; actualHours?: number }) => {
    if (!transitionTarget) return;
    transitionMut.mutate(
      {
        newStatus: transitionTarget,
        cancelReason: input.reason,
        actualHours: input.actualHours,
      },
      {
        onSuccess: () => {
          setTransitionTarget(null);
          transitionMut.reset();
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
            <Link to="/work-orders">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-sm text-muted-foreground">
              {wo ? wo.number : '...'}
            </span>
            <h1 className="text-2xl font-semibold">
              {wo ? wo.title : t('common.loading')}
            </h1>
            {wo && (
              <span
                className={cn(
                  'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                  workOrderStatusColor(wo.status),
                )}
              >
                {t(`workOrder.status.${wo.status}`)}
              </span>
            )}
          </div>
        </div>
        {wo && (
          <div className="flex flex-wrap gap-2">
            {transitions.map((tr) => (
              <Button
                key={tr.to}
                variant={variantFor(tr.to)}
                onClick={() => setTransitionTarget(tr.to)}
                disabled={transitionMut.isPending}
              >
                {t(`workOrder.transition.${tr.to}.button`)}
              </Button>
            ))}
            <WorkOrderCounterAction workOrder={wo} />
            {editable && (
              <>
                <Button asChild variant="outline">
                  <Link to={`/work-orders/${wo.id}/edit`}>
                    <Edit aria-hidden />
                    {t('common.edit')}
                  </Link>
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setConfirmOpen(true)}
                  disabled={deleteMut.isPending}
                >
                  {deleteMut.isPending ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : (
                    <Trash2 aria-hidden />
                  )}
                  {t('common.delete')}
                </Button>
              </>
            )}
          </div>
        )}
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('workOrder.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {wo && (
        <>
          <DetailContent wo={wo} />

          <WorkOrderCostCard workOrderId={wo.id} />

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">
                  {t('workOrder.detail.sectionTimeline')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {eventsLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : (
                  <TimelineCard events={events ?? []} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('workOrder.detail.sectionAssignments')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {assignmentsLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <Skeleton key={i} className="h-9 w-full" />
                    ))}
                  </div>
                ) : (
                  <AssignmentsCard assignments={assignments ?? []} />
                )}
              </CardContent>
            </Card>
          </div>

          <QrCodeDisplay entityType="work_order" entityId={wo.id} />
        </>
      )}

      {wo && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('workOrder.delete.title')}
          description={t('workOrder.delete.description', { number: wo.number })}
          confirmLabel={t('workOrder.delete.confirm')}
          variant="destructive"
          loading={deleteMut.isPending}
          onConfirm={onConfirmDelete}
        />
      )}

      {wo && transitionTarget && (
        <TransitionModal
          open={transitionTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setTransitionTarget(null);
              transitionMut.reset();
            }
          }}
          title={t(`workOrder.transition.${transitionTarget}.title`)}
          description={t(`workOrder.transition.${transitionTarget}.description`)}
          confirmLabel={t(`workOrder.transition.${transitionTarget}.confirm`)}
          variant={
            transitionTarget === 'cancelled' || transitionTarget === 'rejected'
              ? 'destructive'
              : 'default'
          }
          requireReason={STATUSES_REQUIRING_REASON.includes(transitionTarget)}
          showActualHours={transitionTarget === 'pending_review'}
          loading={transitionMut.isPending}
          errorMessage={transitionErrorMessage}
          onConfirm={onConfirmTransition}
        />
      )}
    </div>
  );
}
